import "normalize.css"
import React, { useCallback, useEffect } from 'react';
import {App} from "@soap/baseweb";
import {
    bus,
    CenterSpinner,
    addTranslations,
    defaultSoapTranslations,
    ProtectedRoute,
    useDomainEvent,
    useAuth,
    useLazyQuery
} from '@soap/modules';
import {createTheme} from 'baseui';
import en_default from './translations/en-default'
import ReactDOM from "react-dom/client";
import {Route, Routes, useLocation} from "react-router";
import {Help} from "./z60-pagecomp/help";
import {Pricing} from "./z60-pagecomp/pricing";
import {Order} from "./z60-pagecomp/order";
import {TakeAssessment} from "./z70-shadcn/components/take-assessment/take-assessment";
import {Home} from "./z60-pagecomp/home";
import {AuthLoginRedirect} from "./z60-pagecomp/auth-login-redirect";
import {LogoutPage} from "./z60-pagecomp/logout-page";
import {ErrorComponent} from "./z50-domaincomp/error-component";
import {EnableILC} from "./z50-domaincomp/enableILC";
import {JoinOurTeam} from "./z60-pagecomp/join-our-team";
import {AcceptInvitation} from "./z60-pagecomp/accept-invitation";
import {AcceptInvitation2} from "./z60-pagecomp/accept-invitation2";
import {DiagramSamplesPage} from "./z70-shadcn/pages/DiagramSamplesPage";
import {OrderConfirmation} from "./z60-pagecomp/order-confirmation";
import {DashboardPage} from "./z70-shadcn/pages/DashboardPage";
import {HelpPage} from "./z70-shadcn/pages/HelpPage";
import {LessonsPage} from "./z70-shadcn/pages/LessonsPage";
import {SettingsPage} from "./z70-shadcn/pages/SettingsPage";
import {ProfilesPage} from "./z70-shadcn/pages/ProfilesPage";
import {AddStudentPage} from "./z70-shadcn/pages/AddStudentPage";
import {EditStudentPage} from "./z70-shadcn/pages/EditStudentPage";
import {ReportsPage} from "./z70-shadcn/pages/ReportsPage";
import {AnnualProgressReportPage} from "./z70-shadcn/pages/reports/AnnualProgressReportPage";
import {AssessmentCoverageReportPage} from "./z70-shadcn/pages/reports/AssessmentCoverageReportPage";
import {LearningActivityAuditReportPage} from "./z70-shadcn/pages/reports/LearningActivityAuditReportPage";
import {LywLayout} from "./z70-shadcn/components/LywLayout";
import {TourProviderWrapper} from "./z70-shadcn/components/TourProviderWrapper";
import {StudentDashboardPage} from "./z70-shadcn/pages/students/StudentDashboardPage";
import {StudentSettingsPage} from "./z70-shadcn/pages/students/StudentSettingsPage";
import {CompletedAssessmentsPage} from "./z70-shadcn/pages/students/CompletedAssessmentsPage";
import {SelectedStudentProvider} from "./z70-shadcn/hooks/useSelectedStudent";
import {useUserRole} from "./hooks/useUserRole";
import {useLoginRedirect} from "./hooks/useLoginRedirect";
import { lywAuthRecovery } from "./constants/authRecovery";
import {CenteredColumn} from "./z40-uicomp/centered-column";
import {Toaster} from "./z70-shadcn/ui/toaster";
import {TrialPromoCapture} from "./components/TrialPromoCapture";
import {
    BUS_CHANNEL_INTERNAL,
    NEW_ACCOUNT_TOKEN_REFRESH_REQUIRED_CODE,
    REFETCH_C166_DOMAIN_EVENT,
    REFETCH_C302_DOMAIN_EVENT,
    TOKENS_REFRESHED_AFTER_FAILURE_EVENT,
} from "./modules/lyw-auth-events";


//* rewire errors for baseweb 15
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;
console.warn = (...args) => {
    const message = args[0];
    if (typeof message === 'string' &&
        (message.includes('Mixing shorthand and longhand properties within the same style object is unsupported with atomic rendering.'))
    ) {
        // Suppress this specific warning
        return;
    }
    originalConsoleWarn(...args);
};
console.error = (...args) => {
    const message = args[0];
    if (typeof message === 'string' &&
        (message.includes('Support for defaultProps will be removed from function components in a future major release.') ||
            message.includes('findDOMNode is deprecated and will be removed in the next major release.'))
    ) {
        // Suppress this specific warning
        return;
    }
    // For all other messages, call the original console.error
    originalConsoleError(...args);
};

//* check for maint
window.LYW ??= {maintenance: false};

// Auth0 throws when refresh runs after clearAuth() but before redirect completes; SOAP command-handler
// awaits refresh without catch (fix belongs in @soap/modules). Avoid uncaught-rejection noise in dev.
if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (event) => {
        const r = event.reason;
        const msg = typeof r?.message === "string" ? r.message : String(r ?? "");
        if (msg.includes("Missing Refresh Token")) {
            event.preventDefault();
        }
    });
}

//* detect country - prioritize IP detection over locale
const localeOverride = undefined;
const countryCodeFromLocale = getCountryCodeFromLocale();

// Start IP detection immediately with timeout fallback
let countryCodeSet = false;
const IP_DETECTION_TIMEOUT = 2000; // 2 seconds

void getUserIpAddress()
    .then(ipAddress => {
        if (ipAddress) {
            return getCountryByIp(ipAddress);
        }
        return null;
    })
    .then(country => {
        if (country && !localeOverride && !countryCodeSet) {
            console.warn(`Detected Country Code by IP: ${country}.`);
            window.LYW.PaddleCountryCode = country;
            countryCodeSet = true;
            // Dispatch event so components can react
            window.dispatchEvent(new CustomEvent('countryCodeDetected', { detail: { country, method: 'ip' } }));
        }
        return country;
    })
    .catch(error => {
        console.error('Error during IP detection:', error);
        return null;
    });

// Set timeout - if IP detection doesn't complete in time, fall back to locale
setTimeout(() => {
    if (!countryCodeSet && !localeOverride) {
        if (countryCodeFromLocale) {
            console.warn(`Country code based on locale (IP detection timeout): ${countryCodeFromLocale}.`);
            window.LYW.PaddleCountryCode = countryCodeFromLocale;
            countryCodeSet = true;
            window.dispatchEvent(new CustomEvent('countryCodeDetected', { detail: { country: countryCodeFromLocale, method: 'locale' } }));
        } else {
            console.error("Failed to retrieve the user's country code based on locale.");
        }
    }
}, IP_DETECTION_TIMEOUT);

// Handle locale override immediately
if (localeOverride) {
    console.warn(`The country code is ${localeOverride}.`);
    window.LYW.PaddleCountryCode = localeOverride;
    countryCodeSet = true;
}
addTranslations(defaultSoapTranslations);
addTranslations(en_default);

/**
 * After E001Handler refreshes tokens for new-account (TokenRefreshRequired), refetch profile + todos.
 * Listens on the internal bus topic with no conversationId so it does not depend on which message ran first.
 */
function LywRefetchAfterNewAccountTokenRefresh() {
    const triggerRefetchC166 = useDomainEvent({ eventName: REFETCH_C166_DOMAIN_EVENT });
    const triggerRefetchC302 = useDomainEvent({ eventName: REFETCH_C302_DOMAIN_EVENT });

    useDomainEvent({
        channel: BUS_CHANNEL_INTERNAL,
        eventName: TOKENS_REFRESHED_AFTER_FAILURE_EVENT,
        onEventReceived: (payload) => {
            const codes = payload?.errorCodes ?? [];
            const isNewAccount = codes.some(
                (c) =>
                    String(c).toLowerCase() ===
                    NEW_ACCOUNT_TOKEN_REFRESH_REQUIRED_CODE.toLowerCase()
            );
            if (!isNewAccount) {
                return;
            }
            console.warn(
                'LywRefetchAfterNewAccountTokenRefresh: refetching C166 and C302 after new-account token refresh'
            );
            triggerRefetchC166({});
            triggerRefetchC302({});
        },
    });

    return null;
}

/** Dev-only: trigger the global error modal for testing. Add ?triggerApiError=1 to any URL. */
function TriggerApiErrorForTest() {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('triggerApiError') !== '1') return;
        const schema = 'Soap.Interfaces.Messages.E001v1_MessageFailed';
        const payload = {
            e001_ErrorMessage: 'An Error Occurred',
            e001_MessageId: crypto.randomUUID(),
            e001_ErrorCodes: []
        };
        const t = setTimeout(() => {
            bus.publish(bus.channels.events, schema, payload, undefined);
        }, 300);
        return () => clearTimeout(t);
    }, []);
    return null;
}

//* main component
function Index() {

    //* theming
    const overrides = {
        colors: {
            contentOnColor: "#ededed", //style guide off-white
            linkText: "#000000",
            linkVisited: "#000000",
            linkHover: "#000000",
            linkActive: "#000000",
            accent: "#00b366", //vibrant green
            accent50: '#16a1ca', //style guide light blue
            accent100: '#00438f', // style guide dark blue
            accent200: '#f0fff0', // honeydew (for text on the style guide green)
            accent300: '#ededed', //style guide off white
            accent400: '#2aff1d',
            accent500: '#999ea4', //silver
            accent600: '#f5f5f5', //lighter offwhite
            primaryB: "#ffffff",
        },
        typography: {
            primaryFontFamily: 'Lato'
        }
    };
    const theme = createTheme(overrides);
    const loginRedirect = useLoginRedirect();
    const { isStudent } = useUserRole();

    React.useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash.includes('/app') && isStudent) {
                loginRedirect();
            }
        };

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);

        // Also check on mount
        handleHashChange();

        // Cleanup
        return () => window.removeEventListener('hashchange', handleHashChange);
        
    }, [isStudent, loginRedirect]);
    
    return (

        <App theme={theme} errorComponent={<ErrorComponent defaultErrorMessage={"An Error Occurred"}/>}
             useRefreshTokens={true}
             authRecovery={lywAuthRecovery}>
            <Toaster />
            <TrialPromoCapture />
            <LywRefetchAfterNewAccountTokenRefresh />
            {import.meta.env.DEV && <TriggerApiErrorForTest />}
            <TourProviderWrapper>
            <ConditionalSelectedStudentProvider>
            <Routes>
                {/* routes ordered by specific to general */}
                {/* unauthenticated */}
                <Route path="/help" element={<Help/>}/>
                <Route path="/join" element={<JoinOurTeam/>}/>
                <Route path="/learning-plans" element={<Pricing/>}/>
                <Route path="/contact-us" element={<Help/>}/>
                <Route path="/accept-invite/:invitationId" element={<AcceptInvitation/>}/>
                <Route path="/accept-invite2/:invitationId" element={<AcceptInvitation2/>}/>
                <Route path="/take-assessment/:sharedAssessmentLinkId" element={<TakeAssessment/>}/>
                <Route path="/auth-login" element={<AuthLoginRedirect/>}/>
                <Route path="/logout" element={<LogoutPage/>}/>

                <Route
                    path="/order/:id"
                    element={
                        <Order/>
                    }
                />
                <Route
                    path="/order-complete/:id"
                    element={<OrderConfirmation/>}
                />
                <Route path="/app" element={<ProtectedRoute path="/app"><LywLayout/></ProtectedRoute>}>
                    <Route index element={<DashboardPage/>}/>
                    <Route path="help" element={<HelpPage/>}/>
                    <Route path="lessons/add-topics" element={<LessonsPage/>}/>
                    <Route path="lessons" element={<LessonsPage/>}/>
                    <Route path="lessons/create" element={<LessonsPage/>}/>
                    <Route path="lessons/review" element={<LessonsPage/>}/>
                    <Route path="lessons/inprogress" element={<LessonsPage/>}/>
                    <Route path="lessons/completed" element={<LessonsPage/>}/>
                    <Route path="lessons/assess" element={<LessonsPage/>}/>
                    <Route path="lessons/assess/*" element={<LessonsPage/>}/>
                    <Route path="reports/annual-progress" element={<AnnualProgressReportPage/>}/>
                    <Route path="reports/assessment-coverage" element={<AssessmentCoverageReportPage/>}/>
                    <Route path="reports/learning-activity-audit" element={<LearningActivityAuditReportPage/>}/>
                    <Route path="reports" element={<ReportsPage/>}/>
                    <Route path="profiles/students" element={<ProfilesPage/>}/>
                    <Route path="profiles/students/add" element={<AddStudentPage/>}/>
                    <Route path="profiles/students/edit/:id" element={<EditStudentPage/>}/>
                    <Route path="profiles/environment" element={<ProfilesPage/>}/>
                    <Route path="profiles" element={<ProfilesPage/>}/>
                    <Route path="settings" element={<SettingsPage/>}/>
                    <Route path="settings/profile" element={<SettingsPage/>}/>
                    <Route path="settings/billing" element={<SettingsPage/>}/>
                    <Route path="settings/education-system" element={<SettingsPage/>}/>
                    <Route path="settings/external-resources" element={<SettingsPage/>}/>
                    <Route path="settings/users" element={<SettingsPage/>}/>
                    <Route path="settings/users/account-details" element={<SettingsPage/>}/>
                    <Route path="settings/users/members" element={<SettingsPage/>}/>
                    <Route path="settings/users/invitations" element={<SettingsPage/>}/>
                    <Route path="settings/tutors" element={<SettingsPage/>}/>
                </Route>
                {/* Student Routes */}
                <Route
                    path="/students/dashboard"
                    element={
                        <ProtectedRoute path="/students/dashboard">
                            <StudentDashboardPage/>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/students/completed-assessments"
                    element={
                        <ProtectedRoute path="/students/completed-assessments">
                            <CompletedAssessmentsPage/>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/students/settings"
                    element={
                        <ProtectedRoute path="/students/settings">
                            <StudentSettingsPage/>
                        </ProtectedRoute>
                    }
                />

                {import.meta.env.DEV ? (
                    <Route path="/diagram-samples" element={<DiagramSamplesPage />} />
                ) : null}
                <Route path="/enableILC" element={<EnableILC/>}/>
                {/*must be last, routes ordered by specific to general*/}
                <Route path="/" element={<Content/>}/>

            </Routes>
            </ConditionalSelectedStudentProvider>
            </TourProviderWrapper>
        </App>

    );
}

/**
 * Conditionally renders SelectedStudentProvider only for Plan and Reports sections.
 * TourProvider is now at app root so this no longer affects the tour.
 */
function ConditionalSelectedStudentProvider({ children }) {
    const location = useLocation();
    const shouldEnableProvider =
        location.pathname.startsWith('/app/lessons') ||
        location.pathname.startsWith('/app/reports');

    if (shouldEnableProvider) {
        return <SelectedStudentProvider>{children}</SelectedStudentProvider>;
    }
    return <>{children}</>;
}

function Content() {
    //* SOAP
    const {
        authReady,
        isAuthenticated,
    } = useAuth("home.js");

    const loginRedirect = useLoginRedirect();
    const viewStates = {
        loadingSystem: 1,
        notAuthenticated: 2,
        loadingProfile: 3,
        authenticated: 4
    };
    const [viewState, setViewState] = React.useState(viewStates.loadingSystem);
    const [getProfileTrigger, e138GotProfile] = useLazyQuery("C166v1_GetUserProfile");

    useDomainEvent({
        eventName: REFETCH_C166_DOMAIN_EVENT,
        onEventReceived: () => {
            getProfileTrigger();
        },
    });

    React.useEffect(() => {
        
        if (viewState === viewStates.loadingSystem) {
            // If isAuthenticated is undefined/null, stay in loadingSystem state
            if (isAuthenticated === false) {
                setViewState(viewStates.notAuthenticated);
            }
            // Only transition if we have a definitive authentication state
            if (isAuthenticated === true && !e138GotProfile) {
                getProfileTrigger();
                setViewState(viewStates.loadingProfile);
            }
        }
        else if (viewState === viewStates.loadingProfile && e138GotProfile) {
            loginRedirect();
        }
    }, [authReady, isAuthenticated, e138GotProfile, getProfileTrigger, viewState]);

    switch (viewState) {
        case viewStates.loadingSystem:
        case viewStates.notAuthenticated:
            return <Home/>;
        case viewStates.authenticated:
        case viewStates.loadingProfile:
            return <CenteredColumn><CenterSpinner/></CenteredColumn>;
    }


}

ReactDOM.createRoot(document.getElementById('content')).render(<Index/>);

async function getCountryByIp(ipAddress) {
    const apiUrl = `https://api.country.is/${ipAddress}`;

    try {
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (response.ok) {
            const country = data.country;
            return country;
        } else {
            console.error(`Error fetching country: ${data.error}`);
            return null;
        }
    } catch (error) {
        console.error(`Error fetching country: ${error}`);
        return null;
    }
}

function getCountryCodeFromLocale() {
    // Get the user's browser locale
    const userLocale = navigator.language || navigator.userLanguage;
    // Extract the country code from the locale (assuming it follows the format 'en-US' or 'en_US')
    const countryCode = userLocale.split(/[-_]/)[1];
    return countryCode;
}

// Function to get the user's IP address using ipify
async function getUserIpAddress() {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();

        if (response.ok) {
            const ipAddress = data.ip;
            return ipAddress;
        } else {
            console.error(`Error: ${data.error}`);
            return null;
        }
    } catch (error) {
        console.error(`An error occurred: ${error}`);
        return null;
    }
}

    